import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Download,
  Save,
  FileText,
  Loader2,
  Zap,
  Shield,
  MessageSquare,
  CheckCircle2,
  ChevronRight,
  Plus,
  Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useApiClient } from "@/hooks/use-api-client";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { cn } from "@/lib/utils";

export default function AIPlaybookGenerator() {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [description, setDescription] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [step, setStep] = useState<"agent-select" | "input" | "generating" | "preview">("agent-select");
  const [selectedAgent, setSelectedAgent] = useState<"contract_review" | "legal_research" | "document_drafting" | "chatbot">("contract_review");
  const [progress, setProgress] = useState(0);
  const [generatedSkill, setGeneratedSkill] = useState<any>(null);

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error("Por favor, describe qué tipo de playbook necesitas.");
      return;
    }

    setStep("generating");
    setIsGenerating(true);

    // Simulate progress
    const interval = setInterval(() => {
      setProgress(prev => (prev < 90 ? prev + 10 : prev));
    }, 1000);

    try {
      const localizedDescription = `[IMPORTANT: All legal references MUST be based on Colombian law. Specifically, use Art. 1602 of the Colombian Civil Code for contractual force, and NEVER reference Art. 1255 of the Spanish Civil Code or any other foreign jurisdiction.]\n\n${description}`;
      
      const res = await apiClient.post("/api/v1/contract-review/generate-from-scratch", {
        user_description: localizedDescription,
        agent_type: selectedAgent
      });

      if (res.ok) {
        clearInterval(interval);
        setProgress(100);
        setGeneratedSkill(res.data.skill);
        setStep("preview");
        toast.success("¡Playbook diseñado exitosamente!");
      } else {
        throw new Error(res.data?.message || "Error al generar");
      }
    } catch (err: any) {
      clearInterval(interval);
      toast.error(err.message || "No se pudo generar el playbook.");
      setStep("input");
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPDF = () => {
    if (!generatedSkill) return;

    const doc = new jsPDF();
    const margin = 20;
    let y = 20;

    // Title
    doc.setFontSize(22);
    doc.setTextColor(15, 23, 42); // slate-900
    doc.text(generatedSkill.name, margin, y);
    y += 10;

    // Description
    doc.setFontSize(12);
    doc.setTextColor(100, 116, 139); // slate-500
    const splitDesc = doc.splitTextToSize(generatedSkill.description, 170);
    doc.text(splitDesc, margin, y);
    y += splitDesc.length * 7 + 10;

    // Content
    generatedSkill.topics.forEach((topic: any, idx: number) => {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }

      // Topic Name
      doc.setFontSize(16);
      doc.setTextColor(79, 70, 229); // indigo-600
      doc.text(`${idx + 1}. ${topic.name}`, margin, y);
      y += 8;

      // Topic Rules
      doc.setFontSize(11);
      doc.setTextColor(51, 65, 85); // slate-700
      topic.rules.forEach((rule: any) => {
        const splitRule = doc.splitTextToSize(`• ${rule.text}`, 160);
        doc.text(splitRule, margin + 5, y);
        y += splitRule.length * 5 + 2;
      });

      // Example Clause
      if (topic.example_clauses && topic.example_clauses.length > 0) {
        y += 2;
        doc.setFontSize(10);
        doc.setTextColor(148, 163, 184); // slate-400
        doc.text("CLÁUSULA MODELO:", margin + 5, y);
        y += 5;
        doc.setFontSize(10);
        doc.setTextColor(71, 85, 105); // slate-600
        const splitClause = doc.splitTextToSize(topic.example_clauses[0].text, 155);
        doc.text(splitClause, margin + 10, y);
        y += splitClause.length * 5 + 10;
      }

      y += 5;
    });

    doc.save(`${generatedSkill.name.replace(/\s+/g, "_")}.pdf`);
    toast.success("Descargando PDF...");
  };

  const agents = [
    {
      id: "contract_review",
      name: "Contract Review",
      description: "Auditoría de cumplimiento basada en playbooks corporativos.",
      icon: Shield,
      color: "bg-indigo-600",
      lightColor: "bg-indigo-50",
      textColor: "text-indigo-600"
    },
    {
      id: "legal_research",
      name: "Legal Research",
      description: "Metodologías de búsqueda y análisis de jurisprudencia.",
      icon: Search,
      color: "bg-amber-600",
      lightColor: "bg-amber-50",
      textColor: "text-amber-600"
    },
    {
      id: "document_drafting",
      name: "Document Drafting",
      description: "Estilos de redacción y cláusulas estándar para documentos.",
      icon: FileText,
      color: "bg-purple-600",
      lightColor: "bg-purple-50",
      textColor: "text-purple-600"
    },
    {
      id: "chatbot",
      name: "Chatbot Agent",
      description: "Protocolos de interacción y atención legal automatizada.",
      icon: MessageSquare,
      color: "bg-emerald-600",
      lightColor: "bg-emerald-50",
      textColor: "text-emerald-600"
    }
  ];

  return (
    <div className="flex-1 flex flex-col bg-[#f8fafc] min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 p-6 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              if (step === "input") setStep("agent-select");
              else navigate("/dashboard/skills");
            }}
            className="text-slate-500 hover:text-slate-900 rounded-xl"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-indigo-600 border-indigo-100 bg-indigo-50 font-bold px-3 py-1">
              AI Generator (Beta)
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto w-full p-8 mt-4">
        {step === "agent-select" && (
          <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-indigo-600 rounded-3xl flex items-center justify-center mx-auto shadow-xl shadow-indigo-200">
                <Zap className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-4xl font-black text-slate-900 tracking-tight">Elige el Agente Objetivo</h1>
              <p className="text-slate-500 text-lg max-w-xl mx-auto">
                ¿Para qué tipo de asistente quieres diseñar este nuevo set de habilidades?
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {agents.map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => {
                    setSelectedAgent(agent.id as any);
                    setStep("input");
                  }}
                  className={cn(
                    "group relative p-8 rounded-[2.5rem] bg-white border-2 text-left transition-all hover:scale-[1.03] active:scale-[0.98] shadow-xl shadow-slate-200/50",
                    selectedAgent === agent.id ? "border-indigo-600 ring-4 ring-indigo-50" : "border-transparent hover:border-indigo-200"
                  )}
                >
                  <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center mb-6", agent.lightColor)}>
                    <agent.icon className={cn("h-7 w-7", agent.textColor)} />
                  </div>
                  <h3 className="text-xl font-black text-slate-900 mb-2">{agent.name}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{agent.description}</p>
                  
                  <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Plus className="h-6 w-6 text-indigo-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "input" && (
          <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl flex items-center justify-center mx-auto shadow-xl shadow-indigo-200 animate-pulse">
                <Sparkles className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-4xl font-black text-slate-900 tracking-tight">Personaliza tu Skill</h1>
              <p className="text-slate-500 text-lg max-w-xl mx-auto">
                Describe qué debe saber el agente de {selectedAgent.replace('_', ' ')} y diseñaremos las reglas por ti.
              </p>
            </div>

            <div className="bg-white rounded-[2.5rem] p-10 border border-gray-100 shadow-xl shadow-slate-200/50 space-y-6">
              <div className="space-y-3">
                <div className="flex justify-between items-end">
                  <label className="text-slate-700 font-black text-sm uppercase tracking-widest ml-1">Descripción del Objetivo</label>
                  <span className="text-[10px] text-slate-400 font-bold bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-tighter">Powered by MassimoAI</span>
                </div>
                <Textarea
                  placeholder={
                    selectedAgent === "contract_review" 
                      ? "Ej: Necesito un playbook para revisar contratos de software SaaS en Colombia, enfocado en protección de datos, propiedad intelectual y límites de responsabilidad..."
                      : selectedAgent === "legal_research"
                      ? "Ej: Crea una metodología para investigar precedentes sobre despido injustificado en Colombia, priorizando sentencias de la Corte Suprema de los últimos 5 años..."
                      : selectedAgent === "chatbot"
                      ? "Ej: Define un asistente para atención a ciudadanos sobre trámites de tutela, con tono empático pero profesional, guiándolos paso a paso sobre los requisitos..."
                      : "Ej: Define un estilo de redacción para contratos de arrendamiento comercial, incluyendo cláusulas de incremento de canon según el IPC y causales de terminación anticipada..."
                  }
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[200px] text-lg p-6 bg-slate-50/50 border-gray-100 rounded-3xl focus:ring-indigo-500 focus:bg-white transition-all shadow-inner"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={cn("p-4 rounded-2xl border flex gap-3", 
                    selectedAgent === "legal_research" ? "bg-emerald-50/50 border-emerald-100" : 
                    selectedAgent === "chatbot" ? "bg-teal-50/50 border-teal-100" : "bg-indigo-50/50 border-indigo-100"
                )}>
                  {selectedAgent === "legal_research" ? (
                    <Search className="w-5 h-5 text-emerald-600 mt-0.5" />
                  ) : selectedAgent === "chatbot" ? (
                    <MessageSquare className="w-5 h-5 text-teal-600 mt-0.5" />
                  ) : (
                    <Shield className="w-5 h-5 text-indigo-600 mt-0.5" />
                  )}
                  <p className={cn("text-xs font-medium leading-relaxed", 
                    selectedAgent === "legal_research" ? "text-emerald-900" : 
                    selectedAgent === "chatbot" ? "text-teal-900" : "text-indigo-900"
                  )}>
                    {selectedAgent === "legal_research" 
                      ? "Se diseñarán estrategias de búsqueda y criterios de filtrado jurisprudencial." 
                      : selectedAgent === "chatbot"
                      ? "Se definirán protocolos de conversación y reglas de interacción con el usuario."
                      : "Se incluirán reglas de cumplimiento normativo basadas en la legislación local."}
                  </p>
                </div>
                <div className={cn("p-4 rounded-2xl border flex gap-3",
                  selectedAgent === "document_drafting" ? "bg-purple-50/50 border-purple-100" : "bg-blue-50/50 border-blue-100"
                )}>
                  <FileText className={cn("w-5 h-5 mt-0.5", 
                    selectedAgent === "document_drafting" ? "text-purple-600" : "text-blue-600"
                  )} />
                  <p className={cn("text-xs font-medium leading-relaxed",
                    selectedAgent === "document_drafting" ? "text-purple-900" : "text-blue-900"
                  )}>
                    {selectedAgent === "document_drafting"
                      ? "Se generarán bloques de texto estándar y variaciones según el contexto."
                      : selectedAgent === "legal_research"
                      ? "Analizaremos conceptos legales clave para estructurar tu investigación."
                      : selectedAgent === "chatbot"
                      ? "Optimizaremos el tono y la estructura de respuestas para tu público objetivo."
                      : "Recibirás ejemplos de cláusulas modelo para cada tema identificado."}
                  </p>
                </div>
              </div>

              <Button
                onClick={handleGenerate}
                disabled={!description.trim() || isGenerating}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white font-black py-8 rounded-[2rem] text-xl shadow-xl shadow-indigo-200 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Diseñar Playbook Ahora
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </div>
          </div>
        )}

        {step === "generating" && (
          <div className="flex flex-col items-center justify-center py-20 space-y-10 animate-in fade-in duration-500">
            <div className="relative">
              <div className="w-40 h-40 rounded-[3rem] bg-indigo-100 flex items-center justify-center animate-pulse">
                <Sparkles className="h-20 w-20 text-indigo-600" />
              </div>
              <div className="absolute -bottom-4 -right-4 w-16 h-16 rounded-[1.5rem] bg-white shadow-2xl flex items-center justify-center border border-indigo-50">
                <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
              </div>
            </div>

            <div className="text-center space-y-4 w-full max-w-md">
              <h2 className="text-4xl font-black text-slate-900">Diseñando Conocimiento</h2>
              <p className="text-slate-500 text-lg leading-relaxed">
                {selectedAgent === "legal_research" 
                  ? "Estamos estructurando metodologías de búsqueda y análisis jurisprudencial..." 
                  : selectedAgent === "document_drafting"
                  ? "Estamos diseñando bloques de redacción y lógica de documentos..."
                  : "Estamos analizando las mejores prácticas de la industria para crear tu set de reglas..."}
              </p>

              <div className="pt-8 space-y-3">
                <div className="flex justify-between text-xs font-black text-indigo-600 uppercase tracking-widest">
                  <span>{selectedAgent === "legal_research" ? "Mapeando Jurisprudencia" : "Generando Capas Legales"}</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-4 bg-indigo-100 rounded-full" />
              </div>
            </div>
          </div>
        )}

        {step === "preview" && generatedSkill && (
          <div className="space-y-8 animate-in zoom-in-95 duration-500 pb-12">
            <div className="flex flex-col md:flex-row gap-6 items-center justify-between bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-slate-900">¡Diseño Completado!</h2>
                  <p className="text-slate-500">Tu nuevo skill está listo para ser usado y descargado.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={downloadPDF}
                  variant="outline"
                  className="bg-white border-slate-200 text-slate-700 font-bold py-6 px-8 rounded-2xl hover:bg-slate-50"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Descargar PDF
                </Button>
                <Button
                  onClick={() => navigate("/dashboard/skills")}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-6 px-8 rounded-2xl shadow-lg shadow-indigo-200"
                >
                  <Save className="mr-2 h-5 w-5" />
                  Finalizar
                </Button>
              </div>
            </div>

            {/* Document Preview */}
            <div className="bg-white border border-gray-200 shadow-2xl rounded-[1rem] overflow-hidden">
              <div className="bg-slate-50 border-b border-gray-100 p-4 flex items-center justify-between px-8">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Vista Previa del Documento</span>
                <div className="flex gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
                </div>
              </div>

              <div className="p-16 max-w-[800px] mx-auto space-y-12 bg-white">
                {/* Visual Page Design */}
                <div className="space-y-4 border-b-2 border-indigo-600 pb-8">
                  <h1 className="text-4xl font-black text-slate-900 leading-tight">{generatedSkill.name}</h1>
                  <p className="text-slate-500 text-lg leading-relaxed">{generatedSkill.description}</p>
                </div>

                <div className="space-y-12">
                  {generatedSkill.topics.map((topic: any, idx: number) => (
                    <div key={idx} className="space-y-6">
                      <div className="flex items-center gap-4">
                        <span className="text-3xl font-black text-indigo-100">{String(idx + 1).padStart(2, '0')}</span>
                        <h3 className="text-2xl font-black text-slate-800">{topic.name}</h3>
                      </div>

                      <div className="grid grid-cols-1 gap-4 ml-12">
                        <div className="space-y-3">
                          <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Reglas de Negocio</h4>
                          <ul className="space-y-3">
                            {topic.rules.map((rule: any, rIdx: number) => (
                              <li key={rIdx} className="flex gap-3 text-slate-700 leading-relaxed">
                                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 flex-shrink-0" />
                                {rule.text}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {topic.example_clauses && topic.example_clauses.length > 0 && (
                          <div className="mt-4 p-6 bg-slate-50 rounded-2xl border-l-4 border-indigo-600 italic">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 not-italic">Cláusula Recomendada</p>
                            <p className="text-slate-600 text-sm leading-relaxed">"{topic.example_clauses[0].text}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-20 text-center border-t border-gray-100">
                  <p className="text-slate-300 text-[10px] font-bold uppercase tracking-[0.3em]">Generado por miasistentelegal AI • {new Date().toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
